export type FieldValue =
  | { kind: 'empty' }
  | {
      kind: 'money' | 'number' | 'text' | 'date' | 'boolean' | 'url' | 'image';
      text: string;
      href?: string;
    }
  | { kind: 'list'; items: FieldValue[] }
  | { kind: 'group'; entries: [string, FieldValue][] };

const ACRONYMS = new Set([
  'vat',
  'mpg',
  'co2',
  'vin',
  'id',
  'url',
  'apr',
  'bhp',
  'ev',
  'kw',
  'nm',
  'mph',
  'uk',
]);
const MONEY_TOKENS = new Set(['price', 'amount', 'deposit', 'cost', 'fee', 'balance', 'payment']);
const METRIC_TOKENS = new Set([
  'mpg',
  'co2',
  'g',
  'km',
  'kg',
  'mph',
  'bhp',
  'cc',
  'kw',
  'nm',
  'percent',
  'rating',
  'id',
]);

const isMoneyKey = (key: string): boolean => {
  const tokens = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return tokens.some((t) => MONEY_TOKENS.has(t)) && !tokens.some((t) => METRIC_TOKENS.has(t));
};
const DATE_KEY = /(_at|date|_on)$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?([+-]\d{2}:?\d{2})?)?$/;
const IMAGE_URL = /^https?:\/\/\S+\.(jpe?g|png|webp|gif|avif)(\?\S*)?$/i;
const URL_RE = /^https?:\/\/\S+$/i;

export const humanizeKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z][a-z]+)(\d)/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.toLowerCase());
  return words
    .map((w, i) =>
      ACRONYMS.has(w) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w,
    )
    .join(' ');
};

const isEmpty = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && !value.trim()) ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

export const classifyValue = (key: string, value: unknown): FieldValue => {
  if (isEmpty(value)) return { kind: 'empty' };
  if (Array.isArray(value)) return { kind: 'list', items: value.map((v) => classifyValue(key, v)) };
  if (typeof value === 'object') {
    return {
      kind: 'group',
      entries: Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        classifyValue(k, v),
      ]),
    };
  }
  if (typeof value === 'boolean') return { kind: 'boolean', text: value ? 'Yes' : 'No' };
  const text = String(value).trim();
  const numeric =
    typeof value === 'number' ? value : /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
  if (!Number.isNaN(numeric) && isMoneyKey(key)) {
    const hasPence = !Number.isInteger(numeric);
    return {
      kind: 'money',
      text: `£${numeric.toLocaleString('en-GB', { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`,
    };
  }
  if (typeof value === 'number') return { kind: 'number', text: value.toLocaleString('en-GB') };
  if (IMAGE_URL.test(text)) return { kind: 'image', text, href: text };
  if (URL_RE.test(text)) return { kind: 'url', text: text.replace(/^https?:\/\//, ''), href: text };
  if (ISO_DATE.test(text) || (DATE_KEY.test(key) && !Number.isNaN(Date.parse(text)))) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      return {
        kind: 'date',
        text: text.includes('T') ? date.toLocaleString('en-GB') : date.toLocaleDateString('en-GB'),
      };
    }
  }
  return { kind: 'text', text };
};

/** Number of non-empty leaf values (shown on the "All details" tab). */
export const countFields = (data: unknown): number => {
  if (isEmpty(data)) return 0;
  if (Array.isArray(data)) return data.reduce<number>((n, v) => n + countFields(v), 0);
  if (typeof data === 'object') {
    return Object.values(data as object).reduce<number>((n, v) => n + countFields(v), 0);
  }
  return 1;
};
